import { useState } from "react";
import { BarcodeScannerModal } from "../../../../common/components/forms/inputs/BarcodeField/BarcodeScannerModal";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import styles from "./InventoryTransferAddProductRow.module.scss";

interface IAddProductRowProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  suggestions: ISelectFieldModel[];
  hasSearched: boolean;
  onSuggestionSelect: (id: number) => void;
  onBarcodeScanned: (value: string) => void;
  onOpenQuickAdd: () => void;
}

/**
 * "Add product" row above the lines table.
 *
 * Uses a plain controlled input (not BarcodeField) so the value lives in the
 * parent hook (`useInventoryTransferProductSearch`) rather than in a separate
 * form context. USB scanners still work because they focus the input and
 * type + press Enter — we handle Enter ourselves and call onBarcodeScanned.
 * The camera icon opens the shared BarcodeScannerModal directly.
 */
export const InventoryTransferAddProductRow = ({
  searchValue,
  onSearchChange,
  suggestions,
  hasSearched,
  onSuggestionSelect,
  onBarcodeScanned,
  onOpenQuickAdd,
}: IAddProductRowProps) => {
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (searchValue.trim().length > 0) {
        onBarcodeScanned(searchValue.trim());
      }
    }
  };

  const handleScannerResult = (value: string) => {
    setIsScannerOpen(false);
    onBarcodeScanned(value);
  };

  const noResults = isFocused && hasSearched && suggestions.length === 0 && searchValue.trim().length > 0;
  const showDropdown = isFocused && (suggestions.length > 0 || noResults);

  return (
    <div className={styles.root}>
      <div className={styles.searchWrap}>
        <div className={styles.inputRow}>
          <Icon type={IconType.Search} width="18px" height="18px" color="#9ca3af" />
          <input
            type="text"
            className={styles.input}
            placeholder="Сканирай баркод или въведи име на продукт..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setTimeout(() => setIsFocused(false), 150)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
          <button
            type="button"
            className={styles.cameraBtn}
            onClick={() => setIsScannerOpen(true)}
            aria-label="Сканиране с камера"
          >
            <Icon type={IconType.Camera} width="20px" height="20px" />
          </button>
        </div>

        <RenderWhen condition={showDropdown}>
          <div className={styles.dropdown}>
            {suggestions.map((s) => (
              <div
                key={s.id}
                className={styles.suggestion}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSuggestionSelect(s.id);
                }}
              >
                {s.name}
              </div>
            ))}
            <RenderWhen condition={noResults}>
              <div className={styles.noResults}>Няма намерени продукти.</div>
            </RenderWhen>
          </div>
        </RenderWhen>
      </div>

      <DefaultButton
        label="+ Нов продукт"
        design="outline"
        onClick={onOpenQuickAdd}
        type="button"
      />

      <BarcodeScannerModal
        visible={isScannerOpen}
        onHide={() => setIsScannerOpen(false)}
        onScanned={handleScannerResult}
      />
    </div>
  );
};
